import React, { useEffect, useLayoutEffect, useRef } from "react";
import type { GetStaticPropsContext, NextPage } from "next";
import { Box, Grid, Typography, styled } from "@mui/material";

import HeadMeta, { IHeadMetaComponent } from "@components/head-meta";
import WsPhantomNav from "@components/a1-refactor/typography/WsPhantomNav";
import FormWithHostPost from "@components/form-with-host-post";
import FormHotpots from "shared/models/const/form-hotpots";

// Imágenes disponibles (versión 1 - JPG):
// banner-hipotecario-1563x834.jpg  - 1563×834px  (Mobile pequeño)
// banner-hipotecario-1771x834.jpg  - 1771×834px  (Mobile grande)
// banner-hipotecario-3200x1251.jpg - 3200×1251px (Tablet)
// banner-hipotecario-3750x1251.jpg - 3750×1251px (Desktop)
// banner-hipotecario-4267x1251.jpg - 4267×1251px (Desktop grande)
// banner-hipotecario-6000x1250.jpg - 6000×1250px (XL)
//
// Imágenes disponibles (versión 2 - PNG):
// banner-hipotecario-562x300_2.png  - 562×300px   (Mobile pequeño)
// banner-hipotecario-637x300_2.png  - 637×300px   (Mobile)
// banner-hipotecario-767x300_2.png  - 767×300px   (Tablet)
// banner-hipotecario-899x300_2.png  - 899×300px   (Desktop pequeño)
// banner-hipotecario-1023x300_2.png - 1023×300px  (Desktop)
// banner-hipotecario-1441x300_2.png - 1441×300px  (XL)

const StyledBannerBox = styled(Box)(() => ({
  cursor: "pointer",
  width: "100%",
  height: 200,
  backgroundImage:
    "url('/images/v2/bpd-2025/banner-hipotecario-562x300_2.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  "@media (min-width: 300px)": {
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-562x300_2.png')",
    height: 150,
  },
  "@media (min-width: 350px)": {
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-562x300_2.png')",
    height: 185,
  },
  "@media (min-width: 400px)": {
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-637x300_2.png')",
    height: 210,
  },
  "@media (min-width: 500px)": {
    height: 210,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-637x300_2.png')",
  },
  "@media (min-width: 550px)": {
    height: 220,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-637x300_2.png')",
  },
  "@media (min-width: 600px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-767x300_2.png')",
  },
  "@media (min-width: 700px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-767x300_2.png')",
  },
  "@media (min-width: 800px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-899x300_2.png')",
  },
  "@media (min-width: 900px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-899x300_2.png')",
  },
  "@media (min-width: 950px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1023x300_2.png')",
  },
  "@media (min-width: 1000px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1023x300_2.png')",
  },
  "@media (min-width: 1020px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1023x300_2.png')",
  },
  "@media (min-width: 1200px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1441x300_2.png')",
  },
  "@media (min-width: 1260px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1441x300_2.png')",
  },
  "@media (min-width: 1700px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-6000x1250.jpg')",
    backgroundSize: "contain",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  },
  "@media (min-width: 2000px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1441x300_2.png')",
  },
  "@media (min-width: 2148px)": {
    height: 250,
    backgroundImage:
      "url('/images/v2/bpd-2025/banner-hipotecario-1441x300_2.png')",
  },
}));

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`@lang/${locale}.json`)).default,
    },
  };
}

const MortgageReferralBpdPage: NextPage = () => {
  const metaData: IHeadMetaComponent = {
    title: "mortgageReferralBpd",
    description: "mortgageReferralBpd",
    url: "/referimiento-hipotecario-remax-bpd",
    og_type: "page-mortgage-referral-bpd",
  };
  const refMain = useRef<HTMLDivElement | null>(null);
  const refNav = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <HeadMeta {...metaData} />
      <WsPhantomNav />
      <Grid
        container
        bgcolor="var(--color-brand-cream-primary)"
        justifyContent="center"
        alignContent={"flex-start"}
        alignItems="center"
        sx={{
          minHeight: "calc(100dvh - 70px)",
        }}
        display={"flex"}
        ref={refMain}
      >
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          item
          xs={12}
          bgcolor="var(--color-brand-blue-secondary)"
          overflow="hidden"
          height={{ xs: 'auto', md: "auto" }}
          id="nav"
          ref={refNav}
          columnGap={{ xs: 1.5, md: 3.5 }}
          px={{ xs: 0, md: 0 }}
          py={{ xs: 2, md: 2 }}
          pb={{ xs: 2, md: 2 }}
        >
          <Box
            component="img"
            src="/images/v2/pic/imagen-09.png"
            sx={{
              display: "block",
              width: {
                xs: 96,
                sm: 120,
                md: 150,
              },
              height: "auto",
            }}
          />

          <Box
            sx={{
              width: "5px",
              height: { xs: 68, md: 88 },
              backgroundColor: "rgba(255,255,255,0.9)",
            }}
          />

          <Box
            sx={{
              maxWidth: { xs: 245, sm: 320, md: 520 },
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 500,
                lineHeight: 1.15,
                fontSize: {
                  xs: "0.88rem",
                  sm: "1.05rem",
                  md: "1.45rem",
                },
              }}
            >
              Encuentra a las mejores propiedades de República Dominicana y
              recibe asesoría personalizada.
            </Typography>
          </Box>
        </Grid>
        <Box
          sx={{
            width: "100%",
            maxWidth: 720,
            margin: "0 auto",
          }}
        >
          <FormWithHostPost {...FormHotpots.MORTGAGE_REFERRAL_BPD} />
        </Box>

        {/* Responsive Promotional Banner */}
        <Box
          sx={{
            width: "100%",
            bgcolor: "#012269",
            height: "auto",
          }}
        >
          <StyledBannerBox
            role="img"
            aria-label="Banner Hipotecario RE/MAX BPD"
            onClick={() => {
              window.open(
                "https://popularenlinea.com/Personas/Paginas/prestamos/content-sections/REMAX/index.aspx",
                "_blank",
                "noopener,noreferrer"
              );
            }}
          />
        </Box>

      </Grid>
    </>
  );
};

export default MortgageReferralBpdPage;
